import React, { useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const ButtonsContainer = styled.div`
  position: relative;
  width: 90%;
  max-width: 900px;
  margin: 0 auto;
  padding-top: 2rem;
  background: transparent;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    width: 95%;
    padding-top: 1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.8rem;
  }
`;

const DividingLine = styled.div`
  width: 1px;
  height: 40px;
  background-color: white;
  margin: 0 2rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Button = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: color 0.3s ease;
  color: white;
`;

const ButtonText = styled.div`
  font-size: 2rem;
  font-family: "Moret";
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  height: 2.5rem;
  overflow: hidden;
  display: flex;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    height: 2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.2rem;
    height: 1.8rem;
  }
`;

const CharacterWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const CharacterContainer = styled(motion.span)`
  display: inline-block;
`;

function RollingText({ text, isHovered }) {
  const characters = text.split("");

  return (
    <ButtonText>
      {characters.map((char, index) => (
        <CharacterWrapper key={index}>
          {/* Top Layer */}
          <CharacterContainer
            initial={{ y: 0 }}
            animate={isHovered ? { 
              y: -60, // Move up
              transition: {
                duration: 0.4,
                ease: [0.45, 0, 0.55, 1],
                delay: index * 0.05, // Stagger the animation for each character
              }
            } : {
              y: 0,
              transition: {
                duration: 0.3,
                delay: index * 0.05,
              }
            }}
          >
            {char}
          </CharacterContainer>

          {/* Bottom Layer (Revealed Text) */}
          <CharacterContainer
            initial={{ y: 60 }} // Start below the top layer
            animate={isHovered ? { 
              y: 0, // Move up to reveal
              transition: {
                duration: 0.4,
                ease: [0.45, 0, 0.55, 1],
                delay: index * 0.05, // Stagger the animation for each character
              }
            } : {
              y: 60, // Move back down
              transition: {
                duration: 0.3,
                delay: index * 0.05,
              }
            }}
            style={{ position: "absolute", top: 0, left: 0 }} // Position it below the top layer
          >
            {char}
          </CharacterContainer>
        </CharacterWrapper>
      ))}
    </ButtonText>
  );
}

function WorkButtons({ excludeButton }) {
  const [hoveredButton, setHoveredButton] = useState(null);
  const navigate = useNavigate();

  const handleButtonClick = (path) => {
    navigate(`/${path.toLowerCase()}`);
  };

  const buttons = [
    { name: 'Design', path: 'design' },
    { name: 'Develop', path: 'develop' },
    { name: 'Engage', path: 'engage' },
    { name: 'Play', path: 'play' }
  ].filter(button => button.path !== excludeButton);

  return (
    <ButtonsContainer>
      <DividingLine />
      <ButtonGroup>
        {buttons.map((button, index) => (
          <React.Fragment key={button.name}>
            <ButtonWrapper>
              <Button
                className="hoverable"
                onMouseEnter={() => setHoveredButton(button.name)}
                onMouseLeave={() => setHoveredButton(null)}
                onClick={() => handleButtonClick(button.path)}
              >
                <RollingText text={button.name} isHovered={hoveredButton === button.name} />
              </Button>
            </ButtonWrapper>
            {index < buttons.length - 1 && <DividingLine />}
          </React.Fragment>
        ))}
      </ButtonGroup>
      <DividingLine />
    </ButtonsContainer>
  );
}

WorkButtons.defaultProps = {
  excludeButton: null
};

export default WorkButtons;